/**
 * states.tsx - Estados padrao de tela: vazio, carregando e erro.
 *
 * Re-tokeniza EmptyState/CarteiraSkeleton/ErroDiscreto da semente
 * contas-ui: vermelho via --color-status-red (+soft), superficies via
 * bg-raised/bg-border, radius via --radius-card. Estados honestos,
 * nunca spinner sobre tela branca nem tela derrubada por erro.
 */

import type { CSSProperties, ReactNode } from "react";
import "./ui-base.css";

// ---------------------------------------------------------------------------
// EmptyState - estado vazio honesto, com acao opcional
// ---------------------------------------------------------------------------

function IconeVazio() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      aria-hidden="true"
      style={{ margin: "0 auto", display: "block" }}
    >
      <rect
        x="4"
        y="4"
        width="32"
        height="32"
        rx="6"
        style={{ stroke: "var(--color-bg-border)" }}
        strokeWidth="1.5"
      />
      <path
        d="M14 20h12M20 14v12"
        style={{ stroke: "var(--color-text-disabled)" }}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div
      style={{
        padding: "3rem 2rem",
        textAlign: "center",
        background: "var(--color-bg-raised)",
        border: "1px dashed var(--color-bg-border)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <div style={{ marginBottom: "0.75rem" }}>{icon ?? <IconeVazio />}</div>
      <p
        style={{
          fontFamily: '"Space Grotesk", sans-serif',
          fontSize: "0.9375rem",
          fontWeight: 600,
          color: "var(--color-text-primary)",
          margin: "0 0 0.375rem",
        }}
      >
        {title}
      </p>
      {description != null && (
        <p
          style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: "0.8125rem",
            color: "var(--color-text-muted)",
            margin: "0 0 1rem",
          }}
        >
          {description}
        </p>
      )}
      {action != null && <div style={{ display: "inline-flex" }}>{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LoadingState - skeleton (KPIs + linhas), generaliza o CarteiraSkeleton
// ---------------------------------------------------------------------------

function blocoSkeleton(altura: string, largura = "100%"): CSSProperties {
  return {
    height: altura,
    width: largura,
    background: "var(--color-bg-raised)",
    borderRadius: "var(--radius)",
    border: "1px solid var(--color-bg-border)",
  };
}

export function LoadingState({ rows = 5, kpis = 0 }: { rows?: number; kpis?: number }) {
  return (
    <div role="status" aria-label="Carregando" aria-busy="true">
      {kpis > 0 && (
        <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
          {Array.from({ length: kpis }, (_, i) => (
            <div key={i} style={{ ...blocoSkeleton("64px", "150px"), flex: "1 1 150px" }} />
          ))}
        </div>
      )}
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={{
            ...blocoSkeleton("32px"),
            marginBottom: "0.375rem",
            opacity: Math.max(0.2, 1 - i * 0.08),
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ErrorState / InlineError - erro discreto, sem derrubar a tela
// ---------------------------------------------------------------------------

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.5rem 0.875rem",
        background: "var(--color-status-red-soft)",
        border: "1px solid var(--color-status-red)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <span
        style={{
          fontFamily: '"Inter", sans-serif',
          fontSize: "0.8125rem",
          color: "var(--color-status-red)",
          flex: 1,
        }}
      >
        {message}
      </span>
      {onRetry != null && (
        <button
          type="button"
          className="ui-focusable"
          onClick={onRetry}
          style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: "0.75rem",
            fontWeight: 600,
            padding: "0.25rem 0.75rem",
            borderRadius: "var(--radius)",
            border: "1px solid var(--color-status-red)",
            background: "transparent",
            color: "var(--color-status-red)",
            cursor: "pointer",
          }}
        >
          Tentar novamente
        </button>
      )}
    </div>
  );
}

export function InlineError({ message }: { message: string }) {
  return (
    <span
      role="alert"
      style={{
        fontFamily: '"Inter", sans-serif',
        fontSize: "0.75rem",
        color: "var(--color-status-red)",
      }}
    >
      {message}
    </span>
  );
}
